<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { chargesApi, type Charge, type PDFCharge, type XlsxCharges, type ShiftsUniqueDates } from '@/api/charges';
import { type SerializedUser } from '@/api/users';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { createQueryParams } from '@/utils/query-params';
import { useTableFilter } from '@/composables/useTableFilter';
import { formatCurrency } from '@/utils/currency';
import { chargeTaxDocumentButtonProps, chargeTaxDocumentLabel } from '@/utils/tax-documents';
import { mayClip } from '@/utils/names';
import { usePermissions } from '@/composables/usePermissions';
import { useChargesStore } from '@/stores/charges';
import { useGroupFilterStore } from '@/stores/group-filter';
import { storeToRefs } from 'pinia';
import ChargesListDetail from '@/components/charges/charges-list-detail.vue';
import ChargesDatesDialog from '@/components/charges/charges-dates-dialog.vue';
import { chargePdfsApi } from '@/api/charge-pdfs';
import { useDateRangeValidation } from '@/composables/useDateRangeValidation';
import { useLinkedDateRange } from '@/composables/useLinkedDateRange';
import { useDebouncedRef } from '@/composables/useDebouncedRef';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useMobile } from '@/composables/useMobile';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ChargeCard from '@/components/charges/charge-card.vue';
import BulkExportButton from '@/components/bulk-export-button.vue';
import ChargeClosureEmitDialog from '@/components/charge-closures/charge-closure-emit-dialog.vue';

const { t } = useI18n();
const { hasPermission } = usePermissions();
const { trackChargeEvent } = useAnalytics();
const { tableProps } = useResponsiveTable({ alwaysShowFooter: true });
const { mdAndUp } = useMobile();

const props = defineProps<{
  formattedDates: {
    fromDate: string;
    toDate: string;
  };
}>();

const store = useChargesStore();
if (!store.fromDate) store.fromDate = props.formattedDates.fromDate;
if (!store.toDate) store.toDate = props.formattedDates.toDate;

const { groupIds } = storeToRefs(useGroupFilterStore());

const filters = ref<{
  fromDate: string;
  toDate: string;
}>({
  fromDate: store.fromDate,
  toDate: store.toDate,
});

const tableSortBy = ref<readonly { key: string; order: 'asc' | 'desc' }[]>([]);

const queryParams = computed(() => createQueryParams({
  ...filters.value,
  groupIdIn: groupIds.value,
}));
const { isValidDateRange, dateRangeError } = useDateRangeValidation(filters, true);
// Each request loads every shift of the period on the server: stepping through
// the date inputs asks once the filters settle, not once per step.
const settledQueryParams = useDebouncedRef(queryParams);
// A browser session gets 3 summaries per 10 s (config/initializers/rack_attack.rb):
// a list fetched within that window is current and Filtrar does not ask again.
const SUMMARY_FRESH_MS = 10_000;

const { data: chargesData, refetch, isFetching, isStale } = useQuery({
  queryKey: ['charges', settledQueryParams],
  queryFn: () => chargesApi.index(settledQueryParams.value),
  staleTime: SUMMARY_FRESH_MS,
});
const charges = computed<Charge[]>(() => chargesData.value ?? []);

// Filtrar applies dates that have not settled yet. Settled dates were already
// asked for (a person rarely clicks within the debounce), so it then reloads
// only a list that is no longer fresh.
function applyFilters() {
  if (settledQueryParams.value !== queryParams.value) {
    settledQueryParams.value = queryParams.value;
  } else if (isStale.value) {
    refetch();
  }
}

watch(filters, () => {
  store.fromDate = filters.value.fromDate;
  store.toDate = filters.value.toDate;
}, { deep: true });

useLinkedDateRange(filters);

const staticHeaders = [
  { title: 'Paciente', value: 'patient.fullName', sortable: true },
  { title: 'Apoderado', value: 'patient.representative.fullName', sortable: true },
  { title: 'Cantidad de Turnos', value: 'shiftsCount', sortable: true },
  { title: 'Turnos Marcados', value: 'checkedInShiftsCount', sortable: true },
  { title: 'Monto Turnos', value: 'price', sortable: true },
  { title: 'Ajustes', value: 'totalChargeMovements', sortable: true },
  { title: 'Monto a Cobrar', value: 'amountPendingToCharge', sortable: true },
  { title: 'Acciones', value: 'actions', sortable: false, cellProps: { class: 'text-no-wrap' } },
];

const totalShiftsCount = computed(
  () => charges.value.reduce((acc: number, charge: Charge) => acc + charge.shiftsCount, 0),
);
const totalCheckedInShiftsCount = computed(
  () => charges.value.reduce((acc: number, charge: Charge) => acc + charge.checkedInShiftsCount, 0),
);
const totalPrice = computed(
  () => charges.value.reduce((acc: number, charge: Charge) => acc + charge.price, 0),
);
const totalChargeMovements = computed(
  () => charges.value.reduce((acc: number, charge: Charge) => acc + charge.totalChargeMovements, 0),
);
const totalAmountPendingToCharge = computed(
  () => charges.value.reduce((acc: number, charge: Charge) => acc + charge.amountPendingToCharge, 0),
);

function extractFilename(headers: PDFCharge['headers'], fallback = 'download.pdf'): string {
  const disposition = headers['content-disposition'];
  if (!disposition) return fallback;

  const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]*)["']?/);

  return match?.[1] ?? fallback;
}

const pdfDownloadSnackbarAlert = ref(false);
const repPdfError = ref(false);
function downloadPatientPDF(patientCharge: Charge) {
  pdfDownloadSnackbarAlert.value = true;
  chargesApi.show(patientCharge.patient.id, queryParams.value, 'pdf').then((response: PDFCharge) => {
    const { headers: responseHeaders, data, data: { type } } = response;
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = extractFilename(responseHeaders);
    link.click();
    window.URL.revokeObjectURL(url);
  });
}

function downloadRepresentativePDF(representativeId: number, withoutAmounts = false) {
  repPdfError.value = false;
  pdfDownloadSnackbarAlert.value = true;
  chargesApi.showRepresentativePdf(representativeId, queryParams.value, withoutAmounts)
    .then((response: PDFCharge) => {
      const { headers: responseHeaders, data, data: { type } } = response;
      const url = window.URL.createObjectURL(new Blob([data], { type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = extractFilename(responseHeaders);
      link.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => {
      repPdfError.value = true;
    })
    .finally(() => {
      pdfDownloadSnackbarAlert.value = false;
    });
}

interface RepresentativeGroup {
  representativeId: number;
  representativeName: string;
  charges: Charge[];
  shiftsCount: number;
  checkedInShiftsCount: number;
  price: number;
  totalChargeMovements: number;
  amountPendingToCharge: number;
}

function filterChargesBySearch(chargesList: Charge[], searchTerm: string): Charge[] {
  if (!searchTerm) return chargesList;

  const term = searchTerm.toLowerCase();

  return chargesList.filter((charge: Charge) => {
    const searchableText = [
      charge.patient.fullName,
      charge.patient.representative?.fullName,
      String(charge.shiftsCount),
      String(charge.checkedInShiftsCount),
      String(charge.price),
      String(charge.totalChargeMovements),
      String(charge.amountPendingToCharge),
    ].join(' ').toLowerCase();

    return searchableText.includes(term);
  });
}

const emailSnackbarAlert = ref(false);
const emailError = ref(false);
function sendPatientChargesViaEmail(patientCharge: Charge) {
  emailError.value = false;
  emailSnackbarAlert.value = true;
  chargesApi.show(patientCharge.patient.id, queryParams.value, 'json').catch(() => {
    emailSnackbarAlert.value = false;
    emailError.value = true;
  });
}

function localizedDate(date: string) {
  return date.split('-').reverse().join('/');
}

const search = ref('');

function addChargeToGroup(group: RepresentativeGroup, charge: Charge) {
  group.charges.push(charge);
  group.shiftsCount += charge.shiftsCount;
  group.checkedInShiftsCount += charge.checkedInShiftsCount;
  group.price += charge.price;
  group.totalChargeMovements += charge.totalChargeMovements;
  group.amountPendingToCharge += charge.amountPendingToCharge;
}

// Patients without a representative share the 0 bucket: a stable panel value
// and no representative-PDF actions (there is nobody to bill them to).
const NO_REPRESENTATIVE_GROUP_ID = 0;

const chargesGroupedByRepresentative = computed<RepresentativeGroup[]>(() => {
  const groups = new Map<number, RepresentativeGroup>();
  filterChargesBySearch(charges.value, search.value).forEach((charge: Charge) => {
    const rep = charge.patient.representative;
    const key = rep?.id ?? NO_REPRESENTATIVE_GROUP_ID;
    if (!groups.has(key)) {
      groups.set(key, {
        representativeId: key,
        representativeName: rep?.fullName ?? 'Sin apoderado',
        charges: [],
        shiftsCount: 0,
        checkedInShiftsCount: 0,
        price: 0,
        totalChargeMovements: 0,
        amountPendingToCharge: 0,
      });
    }
    addChargeToGroup(groups.get(key)!, charge);
  });

  return Array.from(groups.values());
});

const customFilter = useTableFilter([
  'patient.fullName',
  'patient.representative.fullName',
  'shiftsCount',
  'checkedInShiftsCount',
  'price',
  'totalChargeMovements',
  'amountPendingToCharge',
]);

const xlsxDownloadSnackbarAlert = ref(false);
function downloadChargesXlsx() {
  xlsxDownloadSnackbarAlert.value = true;

  let downloadQueryParams = queryParams.value;
  if (tableSortBy.value.length > 0) {
    const sortBy = JSON.stringify(tableSortBy.value);
    downloadQueryParams = `${queryParams.value}&sort_by=${encodeURIComponent(sortBy)}`;
  }
  if (store.groupByRepresentative) {
    downloadQueryParams = `${downloadQueryParams}&group_by_representative=true`;
  }

  chargesApi.indexAsXlsx(downloadQueryParams).then((response: XlsxCharges) => {
    const { headers: responseHeaders, data, data: { type } } = response;
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = extractFilename(responseHeaders);
    link.click();
    window.URL.revokeObjectURL(url);
  });
}

const whatsappSnackbarAlert = ref(false);
const errorSnackbarAlert = ref(false);
const errorMessage = ref('');

function generateWhatsappMessage(charge: Charge, pdfUrl: string) {
  const message = `Hola ${charge.patient.representative?.firstName}, envío el detalle de los servicios prestados:\n` +
    '------------------\n' +
    `*Paciente*: ${charge.patient.fullName}\n` +
    `*Desde*: ${localizedDate(filters.value.fromDate)}\n` +
    `*Hasta*: ${localizedDate(filters.value.toDate)}\n` +
    '------------------\n' +
    'Puedes ver el detalle en PDF en el siguiente link:\n' +
    `${pdfUrl}`;

  return encodeURIComponent(message);
}

const { mutateAsync: createChargePdf, isPending: isCreatingChargePdf } = useMutation({
  mutationFn: (charge: Charge) => chargePdfsApi.create({
    patientId: charge.patient.id,
    fromDate: filters.value.fromDate,
    toDate: filters.value.toDate,
  }),
});

function clickWhatsappLink(phoneNumber: string, message: string) {
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
  const link = document.createElement('a');
  link.href = whatsappUrl;
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function sendWhatsappMessage(charge: Charge) {
  if (!charge.patient.representative) return;

  whatsappSnackbarAlert.value = true;
  try {
    const chargePdf = await createChargePdf(charge);
    trackChargeEvent(AnalyticsEvents.charges.whatsappSent, {
      period: `${filters.value.fromDate}-${filters.value.toDate}`,
      patientId: charge.patient.id,
    });
    const message = generateWhatsappMessage(charge, `${window.location.origin}/cp/${chargePdf.publicId}`);
    clickWhatsappLink(charge.patient.representative.phoneNumber, message);
  } catch {
    errorMessage.value = 'Hubo un error generando el mensaje de WhatsApp. Por favor, inténtalo nuevamente.';
    errorSnackbarAlert.value = true;
  } finally {
    whatsappSnackbarAlert.value = false;
  }
}

const currentUser = inject<Ref<SerializedUser>>('currentUser');

// A closure freezes this patient's charges for the range on screen and turns
// them into a tax document; only companies issuing through Kuido see it.
// Billing enabled by Kuido and connected to a provider: until then Configuración is
// the only billing surface, so Cobros shows nothing that would end in a blocker.
const issuesTaxDocuments = computed(() => !!currentUser?.value?.companyTaxDocumentsReady);
const canEmitTaxDocuments = computed(() => (
  issuesTaxDocuments.value && hasPermission.value('charges', 'emit')
));
// A company billing through Kuido sends the DTE email (DTE + charges detail)
// once the authority accepts it, so the manual email and WhatsApp sends would
// only send the detail on its own: they give way to "Emitir DTE".
const canSendCharges = computed(() => !issuesTaxDocuments.value);
const emitCharge = ref<Charge | null>(null);
const showEmitDialog = ref(false);
const emitSnackbarAlert = ref(false);
function openEmitDialog(charge: Charge) {
  emitCharge.value = charge;
  showEmitDialog.value = true;
}
// The row's emit action shows the period's emission state, so the list reloads
// as soon as a document is queued. A closure whose document was then refused
// also changed that state: reload when its dialog closes.
const closureCreatedSinceOpen = ref(false);
function onDocumentEmitted() {
  closureCreatedSinceOpen.value = false;
  emitSnackbarAlert.value = true;
  refetch();
}
watch(showEmitDialog, (open) => {
  if (open || !closureCreatedSinceOpen.value) return;
  closureCreatedSinceOpen.value = false;
  refetch();
});

const headers = computed(() => staticHeaders.filter(header => {
  if (header.value === 'checkedInShiftsCount') {
    return currentUser?.value?.companyIncludeCheckedInShiftsOnly;
  } else if (header.value === 'actions') {
    return hasPermission.value('charges', 'show');
  }

  return true;
}));

const groupedHeaders = computed(() => staticHeaders.filter(header => {
  if (header.value === 'patient.representative.fullName') return false;
  if (header.value === 'actions') return false;
  if (header.value === 'checkedInShiftsCount') {
    return currentUser?.value?.companyIncludeCheckedInShiftsOnly;
  }

  return true;
}));

const showCopyDatesDialog = ref(false);
const selectedPatientDates = ref<ShiftsUniqueDates | null>(null);
function openCopyDatesDialog(item: Charge) {
  selectedPatientDates.value = item.shiftsUniqueDates;
  showCopyDatesDialog.value = true;
}

// Detail + email confirmation are hoisted into single dialogs so the desktop
// table rows and the mobile cards can both drive them.
const selectedPatientId = ref<number | null>(null);
const showDetailDialog = ref(false);
// Looked up from the query data so the open dialog stays fresh after
// charge movement mutations refetch the charges.
const selectedCharge = computed(
  () => charges.value.find((charge: Charge) => charge.patient.id === selectedPatientId.value) ?? null,
);
function openDetail(charge: Charge) {
  selectedPatientId.value = charge.patient.id;
  showDetailDialog.value = true;
}

const emailCharge = ref<Charge | null>(null);
const showEmailDialog = ref(false);
// "Ana Soto (ana@…) con copia a Pedro Soto (pedro@…)": the email goes to the
// first billing recipient (the principal) with the rest in copy.
const emailRecipientsText = computed(() => {
  const [principal, ...copied] = (emailCharge.value?.patient.billingRecipients ?? [])
    .map((recipient) => `${recipient.fullName} (${recipient.email})`);
  if (!principal) return '';
  if (copied.length === 0) return principal;
  const copiedText = copied.length === 1 ? copied[0] :
    `${copied.slice(0, -1).join(', ')} y ${copied[copied.length - 1]}`;

  return `${principal} con copia a ${copiedText}`;
});
function openEmailDialog(charge: Charge) {
  emailCharge.value = charge;
  showEmailDialog.value = true;
}
function confirmSendEmail() {
  if (emailCharge.value) sendPatientChargesViaEmail(emailCharge.value);
  showEmailDialog.value = false;
}

const filteredCharges = computed(() => filterChargesBySearch(charges.value, search.value));

const chargePatientIds = computed(() => filteredCharges.value.map((charge) => charge.patient.id));
</script>
<template>
  <v-card class="w-100">
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        Cobros a Clientes
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-row class="justify-start d-flex align-center">
        <v-text-field
          v-model="filters.fromDate"
          class="px-2 v-col-12 v-col-sm-6 v-col-md-3"
          type="date"
          label="Fecha desde"
          variant="outlined"
          density="compact"
          :error-messages="dateRangeError"
        />
        <v-text-field
          v-model="filters.toDate"
          class="px-2 v-col-12 v-col-sm-6 v-col-md-3"
          type="date"
          label="Fecha hasta"
          variant="outlined"
          density="compact"
          :error-messages="dateRangeError"
        />
        <v-btn
          class="mr-1 mb-6 bg-primary"
          prepend-icon="mdi-magnify"
          :disabled="isFetching || !isValidDateRange"
          @click="applyFilters"
        >
          Filtrar
        </v-btn>
        <v-spacer />
        <v-switch
          v-model="store.groupByRepresentative"
          :color="store.groupByRepresentative ? 'primary' : undefined"
          hide-details
          density="compact"
          class="mr-4 mb-6 flex-grow-0"
        >
          <template #prepend>
            <v-icon
              icon="mdi-account-group"
              :color="store.groupByRepresentative ? 'primary' : undefined"
              @click="store.groupByRepresentative = !store.groupByRepresentative"
            />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Agrupar por Apoderado
            </v-tooltip>
          </template>
        </v-switch>
        <v-btn
          icon="mdi-microsoft-excel"
          class="mr-2 mb-6"
          color="primary"
          variant="text"
          density="comfortable"
          :disabled="isFetching || xlsxDownloadSnackbarAlert"
          @click="downloadChargesXlsx"
        >
          <v-icon icon="mdi-microsoft-excel" />
          <v-tooltip
            activator="parent"
            location="top"
          >
            Descargar cobros en Excel
          </v-tooltip>
        </v-btn>
        <v-snackbar
          v-model="xlsxDownloadSnackbarAlert"
          :timeout="2000"
          color="primary"
          rounded="pill"
        >
          Se está generando el archivo Excel, puede tomar unos segundos...
        </v-snackbar>
        <bulk-export-button
          v-if="hasPermission('charges', 'show')"
          export-type="charges"
          :record-ids="chargePatientIds"
          :from-date="filters.fromDate"
          :to-date="filters.toDate"
          :disabled="isFetching || !isValidDateRange"
        />
        <v-btn
          v-if="issuesTaxDocuments"
          href="/charge_closures"
          class="mr-2 mb-6"
          color="primary"
          variant="text"
          prepend-icon="mdi-file-document-multiple-outline"
        >
          DTEs
        </v-btn>
      </v-row>
      <v-row class="justify-end d-flex align-center">
        <v-text-field
          v-model="search"
          label="Buscar"
          class="mx-4 v-col-12 v-col-sm-6"
          prepend-inner-icon="mdi-magnify"
          single-line
          variant="underlined"
          hide-details
        />
        <group-filter-select
          v-model="groupIds"
          hide-details
          class="mr-4 v-col-12 v-col-sm-4"
        />
      </v-row>
    </v-card-text>

    <!-- Desktop: tables (mobile gets cards below) -->
    <template v-if="mdAndUp">
      <!-- Grouped by Representative view -->
      <template v-if="store.groupByRepresentative">
        <v-expansion-panels
          variant="accordion"
          multiple
        >
          <!-- :value keeps the open-state keyed by representative: without it
               Vuetify falls back to index-based bookkeeping, which desyncs as
               groups remount on refetch/search and leaves the last panel
               unopenable (vuetifyjs/vuetify#9336). -->
          <v-expansion-panel
            v-for="group in chargesGroupedByRepresentative"
            :key="group.representativeId"
            :value="group.representativeId"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-3 w-100">
                <div class="d-flex align-center ga-2">
                  <span class="font-weight-bold text-subtitle-1">
                    {{ group.representativeName }}
                  </span>
                  <v-chip
                    size="small"
                    color="primary"
                  >
                    {{ group.charges.length }} {{ group.charges.length === 1 ? 'paciente' : 'pacientes' }}
                  </v-chip>
                </div>
                <v-spacer />
                <div class="d-flex align-center ga-3">
                  <div class="text-center metric-col">
                    <div class="text-caption text-medium-emphasis">
                      Turnos
                    </div>
                    <div class="text-body-2 font-weight-medium">
                      {{ group.shiftsCount }}
                    </div>
                  </div>
                  <template v-if="currentUser?.companyIncludeCheckedInShiftsOnly">
                    <v-divider vertical />
                    <div class="text-center metric-col">
                      <div class="text-caption text-medium-emphasis">
                        Marcados
                      </div>
                      <div class="text-body-2 font-weight-medium">
                        {{ group.checkedInShiftsCount }}
                      </div>
                    </div>
                  </template>
                  <v-divider vertical />
                  <div class="text-center metric-col">
                    <div class="text-caption text-medium-emphasis">
                      Monto
                    </div>
                    <div class="text-body-2 font-weight-medium">
                      {{ formatCurrency(group.price) }}
                    </div>
                  </div>
                  <v-divider vertical />
                  <div class="text-center metric-col">
                    <div class="text-caption text-medium-emphasis">
                      Ajustes
                    </div>
                    <div class="text-body-2 font-weight-medium">
                      {{ formatCurrency(group.totalChargeMovements) }}
                    </div>
                  </div>
                  <v-divider vertical />
                  <div class="text-center metric-col">
                    <div class="text-caption text-medium-emphasis">
                      Total
                    </div>
                    <v-chip
                      color="green-darken-3"
                      size="small"
                    >
                      {{ formatCurrency(group.amountPendingToCharge) }}
                    </v-chip>
                  </div>
                  <v-btn
                    v-if="group.representativeId !== NO_REPRESENTATIVE_GROUP_ID"
                    size="small"
                    variant="text"
                    @click.stop="downloadRepresentativePDF(group.representativeId)"
                  >
                    <v-badge
                      icon="mdi-cash"
                      color="green-lighten-4"
                      text-color="green-darken-3"
                      location="bottom end"
                      offset-x="-2"
                      offset-y="-2"
                    >
                      <v-icon
                        icon="mdi-file-pdf-box"
                        size="20"
                      />
                    </v-badge>
                    <v-tooltip
                      activator="parent"
                      location="top"
                    >
                      PDF con montos
                    </v-tooltip>
                  </v-btn>
                  <v-btn
                    v-if="group.representativeId !== NO_REPRESENTATIVE_GROUP_ID"
                    size="small"
                    variant="text"
                    @click.stop="downloadRepresentativePDF(group.representativeId, true)"
                  >
                    <v-badge
                      icon="mdi-format-list-numbered"
                      color="blue-lighten-4"
                      text-color="blue-darken-2"
                      location="bottom end"
                      offset-x="-2"
                      offset-y="-2"
                    >
                      <v-icon
                        icon="mdi-file-pdf-box"
                        size="20"
                      />
                    </v-badge>
                    <v-tooltip
                      activator="parent"
                      location="top"
                    >
                      PDF sin montos
                    </v-tooltip>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-data-table
                v-bind="tableProps"
                :headers="groupedHeaders"
                :header-props="{ class: 'font-weight-bold' }"
                :items="group.charges"
                density="comfortable"
                class="my-2"
              >
                <template #item.patient.fullName="{ item }">
                  <div class="name-cell text-truncate">
                    {{ item.patient.fullName }}
                  </div>
                  <v-tooltip
                    v-if="mayClip(item.patient.fullName)"
                    activator="parent"
                    location="top"
                  >
                    {{ item.patient.fullName }}
                  </v-tooltip>
                </template>
                <template #item.price="{ item }">
                  {{ formatCurrency(item.price) }}
                </template>
                <template #item.totalChargeMovements="{ item }">
                  {{ formatCurrency(item.totalChargeMovements) }}
                </template>
                <template #item.amountPendingToCharge="{ item }">
                  <v-chip color="primary">
                    {{ formatCurrency(item.amountPendingToCharge) }}
                  </v-chip>
                </template>
                <template
                  v-if="group.charges.length > 1"
                  #body.append
                >
                  <tr>
                    <th class="font-weight-bold">
                      Total
                    </th>
                    <th class="font-weight-bold">
                      {{ group.shiftsCount }}
                    </th>
                    <th
                      v-if="currentUser?.companyIncludeCheckedInShiftsOnly"
                      class="font-weight-bold"
                    >
                      {{ group.checkedInShiftsCount }}
                    </th>
                    <th class="font-weight-bold">
                      {{ formatCurrency(group.price) }}
                    </th>
                    <th class="font-weight-bold">
                      {{ formatCurrency(group.totalChargeMovements) }}
                    </th>
                    <th>
                      <v-chip color="green-darken-3">
                        {{ formatCurrency(group.amountPendingToCharge) }}
                      </v-chip>
                    </th>
                  </tr>
                </template>
              </v-data-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <!-- Ungrouped (original) view -->
      <v-data-table
        v-else
        v-bind="tableProps"
        v-model:sort-by="tableSortBy"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="charges"
        :search="search"
        :custom-filter="customFilter"
        :loading="isFetching"
        loading-text="Cargando los cobros..."
        no-data-text="No hay cobros en este rango de fechas"
        multi-sort
      >
        <template #item.patient.fullName="{ item }">
          <div class="name-cell text-truncate">
            {{ item.patient.fullName }}
          </div>
          <v-tooltip
            v-if="mayClip(item.patient.fullName)"
            activator="parent"
            location="top"
          >
            {{ item.patient.fullName }}
          </v-tooltip>
        </template>
        <template #item.patient.representative.fullName="{ item }">
          <div class="name-cell text-truncate">
            {{ item.patient.representative?.fullName }}
          </div>
          <v-tooltip
            v-if="mayClip(item.patient.representative?.fullName)"
            activator="parent"
            location="top"
          >
            {{ item.patient.representative?.fullName }}
          </v-tooltip>
        </template>
        <template #item.price="{ item }">
          <span class="text-no-wrap">{{ formatCurrency(item.price) }}</span>
        </template>
        <template #item.totalChargeMovements="{ item }">
          <span class="text-no-wrap">{{ formatCurrency(item.totalChargeMovements) }}</span>
        </template>
        <template #item.amountPendingToCharge="{ item }">
          <v-chip
            color="primary"
            class="text-no-wrap"
          >
            {{ formatCurrency(item.amountPendingToCharge) }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            icon="mdi-eye"
            size="small"
            class="me-2"
            @click="openDetail(item)"
          />
          <v-btn
            icon="mdi-printer"
            size="small"
            class="me-2"
            @click="downloadPatientPDF(item)"
          />
          <v-btn
            v-if="canSendCharges && item.patient.billingRecipients?.length"
            icon
            size="small"
            class="me-2"
            data-test="send-charges-email"
            @click="openEmailDialog(item)"
          >
            <v-icon icon="mdi-email-arrow-right" />
            <v-tooltip
              v-if="item.lastEmailSentAt"
              activator="parent"
              location="top"
            >
              Enviado hace {{ item.lastEmailSentAt }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canSendCharges && item.patient.representative?.phoneNumber"
            icon="mdi-whatsapp"
            data-test="send-charges-whatsapp"
            size="small"
            class="me-2"
            :disabled="isCreatingChargePdf"
            @click="sendWhatsappMessage(item)"
          />
          <v-btn
            v-if="currentUser?.companyChargesWithDateDetails"
            icon="mdi-calendar-multiple"
            size="small"
            class="me-2"
            @click="openCopyDatesDialog(item)"
          >
            <v-icon icon="mdi-calendar-multiple" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Copiar fechas de turnos
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canEmitTaxDocuments"
            icon
            size="small"
            class="me-2"
            data-test="emit-tax-document"
            v-bind="chargeTaxDocumentButtonProps(item.taxDocumentStatus)"
            @click="openEmitDialog(item)"
          >
            <v-icon icon="mdi-file-document-check-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ chargeTaxDocumentLabel(item.taxDocumentStatus, t) }}
            </v-tooltip>
          </v-btn>
        </template>
        <template
          v-if="!search"
          #body.append
        >
          <tr>
            <th class="font-weight-bold">
              Total
            </th>
            <th>
              -
            </th>
            <th class="font-weight-bold">
              {{ totalShiftsCount }}
            </th>
            <th
              v-if="currentUser?.companyIncludeCheckedInShiftsOnly"
              class="font-weight-bold"
            >
              {{ totalCheckedInShiftsCount }}
            </th>
            <th class="font-weight-bold">
              {{ formatCurrency(totalPrice) }}
            </th>
            <th class="font-weight-bold">
              {{ formatCurrency(totalChargeMovements) }}
            </th>
            <th>
              <v-chip color="green-darken-3">
                {{ formatCurrency(totalAmountPendingToCharge) }}
              </v-chip>
            </th>
          </tr>
        </template>
      </v-data-table>
    </template>

    <!-- Mobile: card list -->
    <template v-else>
      <v-card
        variant="tonal"
        color="green-darken-3"
        class="mx-3 mb-3 pa-3"
      >
        <div class="d-flex flex-wrap ga-4">
          <div>
            <div class="text-caption">
              Turnos
            </div>
            <div class="font-weight-bold">
              {{ totalShiftsCount }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Monto turnos
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalPrice) }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Ajustes
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalChargeMovements) }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Total a cobrar
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalAmountPendingToCharge) }}
            </div>
          </div>
        </div>
      </v-card>
      <div class="d-flex flex-column ga-2 px-3 pb-3">
        <charge-card
          v-for="charge in filteredCharges"
          :key="charge.patient.id"
          :charge="charge"
          :can-emit-tax-document="canEmitTaxDocuments"
          :can-send-charges="canSendCharges"
          :can-show-document="hasPermission('charges', 'show')"
          @open="openDetail(charge)"
          @pdf="downloadPatientPDF(charge)"
          @email="openEmailDialog(charge)"
          @whatsapp="sendWhatsappMessage(charge)"
          @tax-document="openEmitDialog(charge)"
        />
        <p
          v-if="!filteredCharges.length"
          class="text-center text-medium-emphasis py-6"
        >
          {{ isFetching ? 'Cargando los cobros...' : 'No hay cobros en este rango de fechas' }}
        </p>
      </div>
    </template>
  </v-card>

  <responsive-dialog
    v-model="showDetailDialog"
    max-width="1400"
    scrollable
  >
    <charges-list-detail
      v-if="selectedCharge"
      :charge="selectedCharge"
      @close="showDetailDialog = false"
    />
  </responsive-dialog>

  <responsive-dialog
    v-model="showEmailDialog"
    max-width="800"
    scrollable
  >
    <v-card v-if="emailCharge">
      <v-card-title class="ml-2 align-baseline d-flex">
        Enviar correo de cobro
        <v-spacer />
        <v-btn
          icon="mdi-window-close"
          variant="plain"
          @click="showEmailDialog = false"
        />
      </v-card-title>
      <v-card-text class="mt-4">
        Estás a punto de enviar un correo con el cobro correspondiente al
        paciente <b>{{ emailCharge.patient.fullName }}</b> desde el <b>{{ localizedDate(filters.fromDate) }}</b>
        hasta el <b>{{ localizedDate(filters.toDate) }}</b>.
      </v-card-text>
      <v-card-text>
        Será enviado a <b>{{ emailRecipientsText }}</b>.
      </v-card-text>
      <v-card-text v-if="emailCharge.lastEmailSentAt">
        El último correo de cobro enviado para este paciente fue <b>hace {{ emailCharge.lastEmailSentAt }}</b>.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text="Enviar"
          color="primary"
          data-test="confirm-charges-email"
          @click="confirmSendEmail"
        />
      </v-card-actions>
    </v-card>
  </responsive-dialog>
  <charges-dates-dialog
    v-model="showCopyDatesDialog"
    :dates="selectedPatientDates"
  />
  <charge-closure-emit-dialog
    v-if="emitCharge"
    v-model="showEmitDialog"
    :patient-id="emitCharge.patient.id"
    :patient-name="emitCharge.patient.fullName"
    :from-date="filters.fromDate"
    :to-date="filters.toDate"
    @closure-created="closureCreatedSinceOpen = true"
    @emitted="onDocumentEmitted"
  />
  <v-snackbar
    v-model="emitSnackbarAlert"
    :timeout="5000"
    color="primary"
    rounded="pill"
  >
    DTE enviado a la autoridad tributaria. Revisa su estado en DTEs.
  </v-snackbar>
  <v-snackbar
    v-model="pdfDownloadSnackbarAlert"
    :timeout="2000"
    color="primary"
    rounded="pill"
  >
    Se está generando el PDF, puede tomar unos segundos...
  </v-snackbar>
  <v-snackbar
    v-model="repPdfError"
    :timeout="3000"
    color="error"
    rounded="pill"
  >
    Hubo un error generando el PDF. Por favor, inténtalo nuevamente.
  </v-snackbar>
  <v-snackbar
    v-model="emailSnackbarAlert"
    :timeout="2000"
    color="primary"
    rounded="pill"
  >
    Se está enviando el correo, puede tomar unos segundos...
  </v-snackbar>
  <v-snackbar
    v-model="emailError"
    :timeout="3000"
    color="error"
    rounded="pill"
  >
    Hubo un error enviando el correo. Por favor, inténtalo nuevamente.
  </v-snackbar>
  <v-snackbar
    v-model="whatsappSnackbarAlert"
    color="primary"
    rounded="pill"
  >
    Generando mensaje de WhatsApp...
  </v-snackbar>
  <v-snackbar
    v-model="errorSnackbarAlert"
    :timeout="3000"
    color="error"
    rounded="pill"
  >
    {{ errorMessage }}
  </v-snackbar>
</template>

<style scoped>
.metric-col {
  min-width: 100px;
}

/* Names get a column they cannot outgrow; the tooltip carries the rest. */
.name-cell {
  max-width: 240px;
}
</style>
